import React, { useState, useEffect } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    Chip,
    CircularProgress,
    Divider,
    ListSubheader,
    Autocomplete,
} from '@mui/material';
import {
    Add as AddIcon,
    Business as BusinessIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { Company, createCompany, updateCompany } from '../api/handleCompanies';
import { useAccount } from '../contexts/AccountContext';
import { useAlert } from '../contexts/AlertContext';

interface EnhancedCompanyDropdownProps {
    label: string;
    value: string;
    onChange: (companyId: string) => void;
    companies: Company[];
    allCompanies: Company[];
    requiredRole: 'carrier' | 'supplier' | 'outlet' | 'billto' | 'any';
    onCompaniesRefresh: () => Promise<void>;
    onNewCompany?: (company: Company) => void;
    onCompanyUpdate?: (company: Company) => void;
    error?: boolean;
    helperText?: string;
    fullWidth?: boolean;
    disabled?: boolean;
}

const EnhancedCompanyDropdown: React.FC<EnhancedCompanyDropdownProps> = ({
    label,
    value,
    onChange,
    companies,
    allCompanies,
    requiredRole,
    onCompaniesRefresh,
    onNewCompany,
    onCompanyUpdate,
    error = false,
    helperText,
    fullWidth = true,
    disabled = false,
}) => {
    const { currentAccount, isImpersonating, impersonatedUser } = useAccount();
    const { showAlert } = useAlert();

    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [addRoleDialogOpen, setAddRoleDialogOpen] = useState(false);
    const [selectedCompanyToAddRole, setSelectedCompanyToAddRole] = useState<Company | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [isAddingRole, setIsAddingRole] = useState(false);
    const [newCompanyName, setNewCompanyName] = useState('');
    const [newCompanyDetails, setNewCompanyDetails] = useState({
        address: '',
        city: '',
        state: '',
        zipCode: '',
        phone: '',
        email: '',
        contactPerson: '',
        notes: '',
        alias: '',
    });

    // Find companies that don't have the required role but could be added
    const companiesWithoutRole = requiredRole === 'any' ? [] : allCompanies.filter(company => {
        const hasRole = requiredRole === 'carrier' ? company.isCarrier :
            requiredRole === 'supplier' ? company.isSupplier :
                requiredRole === 'outlet' ? company.isOutlet :
                    requiredRole === 'billto' ? company.isBillTo : false;
        return !hasRole;
    });

    const resetForm = () => {
        setNewCompanyName('');
        setNewCompanyDetails({
            address: '',
            city: '',
            state: '',
            zipCode: '',
            phone: '',
            email: '',
            contactPerson: '',
            notes: '',
            alias: '',
        });
    };

    const handleCreateNewCompany = async () => {
        if (!newCompanyName.trim()) {
            showAlert('Company name is required', 'error');
            return;
        }

        if (!currentAccount) {
            showAlert('No account selected', 'error');
            return;
        }

        setIsCreating(true);
        try {
            const companyData: Omit<Company, 'id' | 'createdAt' | 'updatedAt'> = {
                name: newCompanyName.trim(),
                address: newCompanyDetails.address,
                city: newCompanyDetails.city,
                state: newCompanyDetails.state,
                zipCode: newCompanyDetails.zipCode,
                phone: newCompanyDetails.phone,
                email: newCompanyDetails.email,
                contactPerson: newCompanyDetails.contactPerson,
                isCarrier: requiredRole === 'carrier' || requiredRole === 'any',
                isSupplier: requiredRole === 'supplier' || requiredRole === 'any',
                isOutlet: requiredRole === 'outlet' || requiredRole === 'any',
                isBillTo: requiredRole === 'billto' || requiredRole === 'any',
                notes: newCompanyDetails.notes || (requiredRole === 'any' ? 'Created from freight form' : `Created as ${requiredRole} from freight form`),
                isActive: true,
                accountId: currentAccount.account_id,
                alias: newCompanyDetails.alias,
            };

            const impersonationData = isImpersonating && impersonatedUser && currentAccount ? {
                isImpersonating: true,
                impersonatedUserId: impersonatedUser.id,
                accountId: currentAccount.account_id,
            } : undefined;

            const newCompany = await createCompany(companyData, currentAccount.account_id, impersonationData);

            // Add the new company to state immediately (no page reload)
            if (onNewCompany) {
                onNewCompany(newCompany);
            }

            // Auto-select the new company
            onChange(newCompany.id!);

            setCreateDialogOpen(false);
            resetForm();
            showAlert(`Company "${newCompany.name}" created successfully!`, 'success');
        } catch (error) {
            console.error('Error creating company:', error);
            showAlert(error instanceof Error ? error.message : 'Failed to create company', 'error');
        } finally {
            setIsCreating(false);
        }
    };

    const handleAddRoleToCompany = async () => {
        if (!selectedCompanyToAddRole) return;

        setIsAddingRole(true);
        try {
            const updates: Partial<Company> = requiredRole === 'any' ? {} : {
                [requiredRole === 'carrier' ? 'isCarrier' :
                    requiredRole === 'supplier' ? 'isSupplier' :
                        requiredRole === 'outlet' ? 'isOutlet' : 'isBillTo']: true
            };

            const impersonationData = isImpersonating && impersonatedUser && currentAccount ? {
                isImpersonating: true,
                impersonatedUserId: impersonatedUser.id,
                accountId: currentAccount.account_id,
            } : undefined;

            const updatedCompany = await updateCompany(selectedCompanyToAddRole.id!, updates, impersonationData);

            // Update the company in state immediately (no page reload)
            if (onCompanyUpdate) {
                onCompanyUpdate(updatedCompany);
            }

            // Auto-select the updated company
            onChange(selectedCompanyToAddRole.id!);

            setAddRoleDialogOpen(false);
            setSelectedCompanyToAddRole(null);
            showAlert(`Added ${requiredRole} role to "${selectedCompanyToAddRole.name}"`, 'success');
        } catch (error) {
            console.error('Error adding role to company:', error);
            showAlert(error instanceof Error ? error.message : 'Failed to add role to company', 'error');
        } finally {
            setIsAddingRole(false);
        }
    };

    const getRoleDisplayName = (role: string) => {
        switch (role) {
            case 'carrier': return 'Carrier';
            case 'supplier': return 'Shipper/Supplier';
            case 'outlet': return 'Consignee/Outlet';
            case 'billto': return 'Bill-To';
            case 'any': return 'Company';
            default: return role;
        }
    };

    return (
        <>
            <FormControl fullWidth={fullWidth} error={error} disabled={disabled}>
                <InputLabel>{label}</InputLabel>
                <Select
                    value={value || ''}
                    label={label}
                    onChange={(e) => onChange(e.target.value)}
                >
                    <MenuItem value="">
                        <em>Select {getRoleDisplayName(requiredRole).toLowerCase()}</em>
                    </MenuItem>

                    <ListSubheader>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={() => setCreateDialogOpen(true)}
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{ mb: 1 }}
                        >
                            Create New {getRoleDisplayName(requiredRole)}
                        </Button>
                        {companiesWithoutRole.length > 0 && (
                            <Button
                                startIcon={<EditIcon />}
                                onClick={() => setAddRoleDialogOpen(true)}
                                variant="outlined"
                                size="small"
                                fullWidth
                                color="secondary"
                            >
                                Add {getRoleDisplayName(requiredRole)} Role to Existing Company
                            </Button>
                        )}
                    </ListSubheader>

                    <Divider />

                    {companies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                <Box>
                                    <Typography>{company.name}</Typography>
                                    {company.alias && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            aka {company.alias}
                                        </Typography>
                                    )}
                                </Box>
                                <Box display="flex" gap={0.5}>
                                    {company.isCarrier && <Chip label="C" size="small" variant="outlined" />}
                                    {company.isSupplier && <Chip label="S" size="small" variant="outlined" />}
                                    {company.isOutlet && <Chip label="O" size="small" variant="outlined" />}
                                    {company.isBillTo && <Chip label="B" size="small" variant="outlined" />}
                                </Box>
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
                {helperText && (
                    <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5, ml: 1.5 }}>
                        {helperText}
                    </Typography>
                )}
            </FormControl>

            {/* Create New Company Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={1}>
                        <BusinessIcon />
                        Create New {getRoleDisplayName(requiredRole)}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} pt={1}>
                        <TextField
                            label="Company Name"
                            value={newCompanyName}
                            onChange={(e) => setNewCompanyName(e.target.value)}
                            required
                            fullWidth
                            autoFocus
                        />

                        <TextField
                            label="Alias"
                            value={newCompanyDetails.alias}
                            onChange={(e) => setNewCompanyDetails(prev => ({ ...prev, alias: e.target.value }))}
                            fullWidth
                            helperText="Alternative name users might call this company"
                        />

                        <Typography variant="subtitle2" color="text.secondary">
                            Additional Details (Optional)
                        </Typography>

                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                            <TextField
                                label="Contact Person"
                                value={newCompanyDetails.contactPerson}
                                onChange={(e) => setNewCompanyDetails(prev => ({ ...prev, contactPerson: e.target.value }))}
                                fullWidth
                            />
                            <TextField
                                label="Phone"
                                value={newCompanyDetails.phone}
                                onChange={(e) => setNewCompanyDetails(prev => ({ ...prev, phone: e.target.value }))}
                                fullWidth
                            />
                        </Box>

                        <TextField
                            label="Email"
                            value={newCompanyDetails.email}
                            onChange={(e) => setNewCompanyDetails(prev => ({ ...prev, email: e.target.value }))}
                            fullWidth
                            type="email"
                        />

                        <TextField
                            label="Address"
                            value={newCompanyDetails.address}
                            onChange={(e) => setNewCompanyDetails(prev => ({ ...prev, address: e.target.value }))}
                            fullWidth
                        />

                        <Box display="grid" gridTemplateColumns="1fr 1fr 100px" gap={2}>
                            <TextField
                                label="City"
                                value={newCompanyDetails.city}
                                onChange={(e) => setNewCompanyDetails(prev => ({ ...prev, city: e.target.value }))}
                                fullWidth
                            />
                            <TextField
                                label="State"
                                value={newCompanyDetails.state}
                                onChange={(e) => setNewCompanyDetails(prev => ({ ...prev, state: e.target.value }))}
                                fullWidth
                            />
                            <TextField
                                label="Zip Code"
                                value={newCompanyDetails.zipCode}
                                onChange={(e) => setNewCompanyDetails(prev => ({ ...prev, zipCode: e.target.value }))}
                                fullWidth
                            />
                        </Box>

                        <TextField
                            label="Notes"
                            value={newCompanyDetails.notes}
                            onChange={(e) => setNewCompanyDetails(prev => ({ ...prev, notes: e.target.value }))}
                            fullWidth
                            multiline
                            rows={2}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)} disabled={isCreating}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateNewCompany}
                        variant="contained"
                        disabled={isCreating || !newCompanyName.trim()}
                        startIcon={isCreating ? <CircularProgress size={16} /> : <AddIcon />}
                    >
                        {isCreating ? 'Creating...' : 'Create Company'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Role to Existing Company Dialog */}
            <Dialog
                open={addRoleDialogOpen}
                onClose={() => setAddRoleDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Add {getRoleDisplayName(requiredRole)} Role
                </DialogTitle>
                <DialogContent>
                    <Box pt={1}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Select an existing company to add the {getRoleDisplayName(requiredRole).toLowerCase()} role:
                        </Typography>

                        <Autocomplete
                            options={companiesWithoutRole}
                            getOptionLabel={(option) => option.name}
                            value={selectedCompanyToAddRole}
                            onChange={(_, newValue) => setSelectedCompanyToAddRole(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Company"
                                    fullWidth
                                    margin="normal"
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                        <Typography>{option.name}</Typography>
                                        <Box display="flex" gap={0.5}>
                                            {option.isCarrier && <Chip label="Carrier" size="small" />}
                                            {option.isSupplier && <Chip label="Supplier" size="small" />}
                                            {option.isOutlet && <Chip label="Outlet" size="small" />}
                                            {option.isBillTo && <Chip label="Bill-To" size="small" />}
                                        </Box>
                                    </Box>
                                </li>
                            )}
                        />

                        {selectedCompanyToAddRole && (
                            <Box mt={2} p={2} bgcolor="background.paper" borderRadius={1} border="1px solid" borderColor="divider">
                                <Typography variant="subtitle2" gutterBottom>
                                    Current Roles for "{selectedCompanyToAddRole.name}":
                                </Typography>
                                <Box display="flex" gap={1} flexWrap="wrap">
                                    {selectedCompanyToAddRole.isCarrier && <Chip label="Carrier" size="small" color="primary" />}
                                    {selectedCompanyToAddRole.isSupplier && <Chip label="Supplier" size="small" color="primary" />}
                                    {selectedCompanyToAddRole.isOutlet && <Chip label="Outlet" size="small" color="primary" />}
                                    {selectedCompanyToAddRole.isBillTo && <Chip label="Bill-To" size="small" color="primary" />}
                                    <Chip label={`+ ${getRoleDisplayName(requiredRole)}`} size="small" color="success" variant="outlined" />
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddRoleDialogOpen(false)} disabled={isAddingRole}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleAddRoleToCompany}
                        variant="contained"
                        disabled={isAddingRole || !selectedCompanyToAddRole}
                        startIcon={isAddingRole ? <CircularProgress size={16} /> : <EditIcon />}
                    >
                        {isAddingRole ? 'Adding Role...' : 'Add Role'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EnhancedCompanyDropdown;
